<template>
  <!-- 捆包列表沿用全部/散货列表的订单分组结构，仅移除勾选、保管和出货字段 -->
  <div class="warehouse-list packing-warehouse-list" v-loading="loading">
    <!-- 捆包列表表头 -->
    <div class="warehouse-grid list-header packing-list packing-list-header">
      <!-- 订单号列与全部/散货列表一致 -->

      <div>NO</div>
      <div>{{ $t("message.photo") }}</div>
      <div>{{ $t("message.customerOrderAttributes") }}</div>
      <div>{{ $t("message.businessEditAttributes") }}</div>
      <div>{{ $t("message.purchaseData") }}</div>
      <div>{{ $t("message.remarksField") }}</div>
      <div>{{ $t("message.businessTranslation") }}</div>
      <div>{{ $t("message.businessReply") }}</div>
      <div>{{ $t("message.additionalServices") }}</div>
      <div>{{ $t("message.label") }}</div>
      <div>{{ $t("message.warehousePackingCount") }}</div>
    </div>

    <!-- 每个订单独立分组，支持展开和收起 -->
    <article
      v-for="(order, orderIndex) in orders"
      :key="order.order_sn || order.for_sn || orderIndex"
      class="order-group"
    >
      <!-- 订单头保持全部/散货列表的深色样式，只保留订单号和展开收起按钮 -->
      <header class="order-header packing-order-header">
        <div class="order-main">
          <!-- 捆包列表没有勾选功能，仅保留展开收起按钮 -->
          <el-button
            class="collapse-button"
            size="mini"
            @click="toggleCollapse(order, orderIndex)"
          >
            {{
              isCollapsed(order, orderIndex)
                ? $t("message.warehouseExpand")
                : $t("message.collapse")
            }}
            <i
              :class="
                isCollapsed(order, orderIndex)
                  ? 'el-icon-arrow-down'
                  : 'el-icon-arrow-up'
              "
            />
          </el-button>
          <!-- 订单号点击后跳转订单详情 -->
          <router-link
            v-if="order.order_sn"
            class="order-link"
            :to="'/orderPaymentDetail?order_sn=' + order.order_sn"
            target="_blank"
          >
            {{ order.order_sn }}
          </router-link>
          <span v-else class="order-link">{{ order.for_sn || "-" }}</span>
        </div>
      </header>

      <!-- 订单商品明细，字段顺序与全部/散货列表保持一致 -->
      <div v-show="!isCollapsed(order, orderIndex)" class="order-details">
        <div
          v-for="detail in order.order_detail"
          :key="detail.id"
          class="detail-block"
        >
          <!-- 商品行不显示勾选框、仓库保管情报、已提出数和可提出数 -->
          <div
            class="warehouse-grid detail-row packing-list packing-detail-row"
          >
            <!-- 商品番号 -->
            <div class="number-cell">
              <strong>{{ detail.sorting }}</strong>
            </div>
            <!-- 商品图片 -->
            <div class="photo-cell">
              <el-image class="product-image" :src="detail.pic" fit="cover">
                <!-- 图片加载失败时显示占位图标 -->
                <div slot="error" class="image-placeholder">
                  <i class="el-icon-picture-outline" />
                </div>
              </el-image>
            </div>
            <!-- 客户下单属性 -->
            <div class="attribute-cell">
              <p
                v-for="(attribute, index) in detail.detail"
                :key="'packing-c-' + detail.id + index"
              >
                <span>{{ attribute.key }}：</span
                ><span>{{ attribute.value }}</span>
              </p>
            </div>
            <!-- 业务编辑属性 -->
            <div class="attribute-cell">
              <p
                v-for="(attribute, index) in detail.detail_y"
                :key="'packing-y-' + detail.id + index"
              >
                <span>{{ attribute.key }}：</span
                ><span>{{ attribute.value }}</span>
              </p>
            </div>
            <!-- 购买数据 -->
            <div class="purchase-cell">
              <p>{{ $t("message.quantity") }}：{{ detail.confirm_num }}</p>
              <p>{{ $t("message.unitPrice") }}：{{ detail.confirm_price }}</p>
              <p>
                {{ $t("message.domesticFreight") }}：{{
                  detail.confirm_freight
                }}
              </p>
            </div>
            <!-- 备注栏、业务翻译保持只读，业务回复与散货存放一致可编辑 -->
            <div class="warehouse-textarea-cell">
              <el-input value="" type="textarea" :rows="4" readonly />
            </div>
            <div class="warehouse-textarea-cell">
              <el-input value="" type="textarea" :rows="4" readonly />
            </div>
            <div class="warehouse-textarea-cell">
              <textarea v-model="detail.y_reply" class="native-textarea" rows="4" :disabled="!isAuth(582)"
                @blur="saveBusinessReply(detail)"></textarea>
            </div>
            <div class="service-cell" />
            <!-- 标签信息 -->
            <div class="tag-cell">
              <div
                v-for="(tag, index) in detail.order_detail_tag"
                :key="'packing-tag-' + detail.id + index"
                class="tag-item"
              >
                <span>{{ tag.type }}</span
                ><span>{{ tag.no }}</span>
              </div>
            </div>
            <!-- 捆包数使用 encasementListV3 返回的外层 num -->
            <div class="quantity-cell packing-count-cell">
              {{ detail.packing_count }}
            </div>
          </div>
        </div>
      </div>
    </article>

    <!-- 无数据提示 -->
    <div v-if="!loading && !orders.length" class="empty-list">
      {{ $t("message.warehouseNoData") }}
    </div>
  </div>
</template>

<script>
export default {
  // 捆包列表独立组件
  name: "WarehousePackingList",
  props: {
    // 父页面按订单分组后的捆包数据
    orders: { type: Array, default: () => [] },
    // 当前列表加载状态
    loading: { type: Boolean, default: false },
  },
  data() {
    return {
      // 记录已收起的订单索引
      collapsedOrders: {},
    };
  },
  methods: {
    // 使用订单号作为稳定的展开状态键
    orderKey(order, index) {
      return order.order_sn || order.for_sn || index;
    },
    // 判断订单是否收起
    isCollapsed(order, index) {
      return !!this.collapsedOrders[this.orderKey(order, index)];
    },
    // 切换订单明细展开状态
    toggleCollapse(order, index) {
      const key = this.orderKey(order, index);
      this.$set(this.collapsedOrders, key, !this.collapsedOrders[key]);
    },
    // 保存单番商品业务回复，接口与散货存放一致
    saveBusinessReply(detail) {
      this.$api.clienteleApi.serviceRemarkUpdate({
        order_detail_id: detail.id,
        y_reply: detail.y_reply,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
// 捆包列表沿用普通仓库列表的宽度和网格排版
.packing-warehouse-list {
  width: 100%;
  min-height: 240px;
  min-width: 0;
  overflow: visible;
  color: #333;
}

// 订单分组边框与普通仓库列表一致
.packing-warehouse-list .order-group {
  width: 100%;
  min-width: 0;
  margin-bottom: 0;
  border: 1px solid #ebeef5;
  border-top: 0;
  border-radius: 0;
  overflow: hidden;
}

// 订单头使用普通列表的深色布局
.packing-warehouse-list .order-header {
  display: grid;
  grid-template-columns: 1fr;
  align-items: center;
  width: 100%;
  min-width: 0;
  min-height: 42px;
  column-gap: 20px;
  padding: 6px 26px;
  box-sizing: border-box;
  background: #56576a;
  color: #fff;
  font-size: 13px;
  overflow: hidden;
}

// 订单头内部按钮和订单号排列
.packing-warehouse-list .order-main {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: max-content;
  white-space: nowrap;
}

// 展开收起按钮完全沿用普通列表尺寸
.packing-warehouse-list .collapse-button {
  height: 20px;
  margin: 0 60px 0 10px;
  padding: 1px 6px;
  border-color: #e4e7ed;
  color: #606266;
  font-size: 11px;
  line-height: 16px;
}

// 深色订单头中的订单号颜色
.packing-warehouse-list .order-link {
  flex: none;
  color: #f4da45;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
}

// 商品明细容器与普通列表保持一致
.packing-warehouse-list .detail-block {
  position: relative;
  width: 100%;
  min-width: 0;
  overflow: hidden;
}

// 商品行白底、边框和最小高度
.packing-warehouse-list .detail-row {
  align-items: stretch;
  min-height: 94px;
  border-bottom: 1px solid #ebeef5;
  background: #fff;
}

// 订单详情区域不额外添加间距
.packing-warehouse-list .order-details {
  width: 100%;
  min-width: 0;
}

// 捆包列表移除勾选、保管和出货字段后的 12 列比例
.packing-warehouse-list .warehouse-grid {
  display: grid;
  /* 直接沿用 index.vue 中捆包列表的 12 列比例 */
  grid-template-columns: 5% 6% 7% 12% 12% 10% 10% 10% 8% 10% 5% 5%;
  width: 100%;
  min-width: 0;
}

// 表头样式与普通仓库列表一致
.packing-list-header {
  position: sticky;
  top: 0;
  z-index: 5;
  min-height: 48px;
  align-items: center;
  background: #eef0f5;
  border: 1px solid #e4e7ed;
  color: #606266;
  font-size: 12px;
  font-weight: 600;
  text-align: center;
}

// 表头单元格内边距与普通列表一致
.packing-list-header > div {
  min-width: 0;
  padding: 0 6px;
  box-sizing: border-box;
}

// 订单头沿用普通列表深色背景
.packing-order-header {
  min-height: 42px;
}

// 商品行沿用普通列表边框和字体
.packing-detail-row {
  min-height: 94px;
  align-items: stretch;
  border-bottom: 1px solid #ebeef5;
  color: #303133;
  font-size: 14px;
  background: #fff;
}

// 商品行单元格统一左对齐和内边距
.packing-detail-row > div,
.packing-detail-row > a,
.packing-detail-row > span {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  padding: 10px 6px;
  box-sizing: border-box;
  overflow: hidden;
}

// 商品订单号链接样式
.detail-order-sn-link {
  color: #c7252b;
  text-decoration: underline;
  word-break: break-all;
}

// 图片尺寸与普通列表保持一致
.product-image {
  width: 60px;
  height: 60px;
  background: #dcdcdc;
}

// 图片单元格使用普通列表的紧凑内边距
.packing-detail-row .photo-cell {
  padding: 8px 4px !important;
}

// 属性和购买数据靠左纵向排列
.attribute-cell,
.purchase-cell,
.warehouse-textarea-cell {
  flex-direction: column;
  align-items: flex-start !important;
  justify-content: center;
  text-align: left;
}

// 商品编号列保持普通列表的纵向居中布局
.packing-detail-row .number-cell {
  position: relative;
  flex-direction: column;
  gap: 8px;
}

// 文本框区域直接沿用普通列表的 100px 高度
.packing-detail-row .warehouse-textarea-cell {
  width: 100%;

  .native-textarea {
    width: 100%;
    height: 100px;
    min-height: 100px !important;
    padding: 8px;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    color: #606266;
    font-size: 14px;
    line-height: 1.4;
    outline: none;
    resize: none;
    box-sizing: border-box;

    &:focus {
      border-color: #c7252b;
    }

    &:disabled,
    &[readonly] {
      background: #f5f7fa;
      color: #606266;
    }
  }
}

.packing-detail-row /deep/ .el-textarea__inner {
  height: 100px;
  min-height: 100px !important;
  padding: 8px;
  resize: none;
}

// 标签信息按普通列表纵向显示
.packing-detail-row .tag-cell {
  flex-direction: column;
  align-items: flex-start !important;
  justify-content: center;
  gap: 7px;
}

.packing-detail-row .tag-item {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding-bottom: 6px;
  border-bottom: 1px dashed #dcdfe6;
}

.attribute-cell p,
.purchase-cell p {
  width: 100%;
  margin: 0;
  line-height: 1.45;
}

// 捆包数单元格居中显示
.packing-count-cell {
  justify-content: center;
  font-weight: 600;
}
</style>
